import Chip from '@/components/chip';
import NoneChip from '@/components/chip/none-chip';
import StatusChip from '@/components/chip/status-chip';
import ContextProvidersCard from '@/components/context-providers-card';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import TextInput from '@/components/form/text-input';
import PrivateModelsCard from '@/components/private-models-card';
import InlineToast from '@/components/toasts/inline';
import { JsonObject } from '@/lib/helpers';
import {
  ConnectorApiKeyConfig,
  ConnectorFeature,
  ConnectorFeatureLabels,
  getTypedConnector,
  ProxyConnectorAuthTypeOptions,
} from '@/lib/models/connector';
import { AwsConfig } from '@/lib/models/connector/aws-connector.model';
import { ConnectorModel } from '@/lib/models/connector/connector.model';
import { NetskopeConfig } from '@/lib/models/connector/netskope-connector.model';
import { SharepointConfig } from '@/lib/models/connector/sharepoint-connector.model';
import { WebcrawlerConfig } from '@/lib/models/connector/webcrawler-connector.model';
import { ZscalerConfig } from '@/lib/models/connector/zscaler-connector.model';
import { PrivateModelModel } from '@/lib/models/private-model/private-model.model';
import { ContextProviderModel } from '@/lib/models/context-provider/context-provider.model';
import { Box, Stack } from '@mui/material';
import { FC, useMemo } from 'react';
import { AzureOpenAIConnectorModel } from '@/lib/models/connector/azure-openai-connector.model';
import { ProxyConfig, ProxyConnectorModel } from '@/lib/models/connector/proxy-connector.model';
import {
  CloudflareConfig,
  CloudflareConnectorModel,
} from '@/lib/models/connector/cloudflare-connector.model';
import { TailscaleConfig } from '@/lib/models/connector/tailscale-connector.model';
import { AppConfig } from '@/lib/models/connector/app-connector.model';
import UserLink from '@/components/user-link';
import { UserModel } from '@/lib/models/user.model';
import { AzureFoundryConfig } from '@/lib/models/connector/azure-foundry-connector.model';
import { CloudflareAIConfig } from '@/lib/models/connector/cloudflare-ai-connector.model';

interface Props {
  privateModels: PrivateModelModel[];
  contextProviders: ContextProviderModel[];
  connector: ConnectorModel;
  canViewArn: boolean;
}

const ConnectorReadonlyView: FC<Props> = ({
  connector,
  privateModels,
  contextProviders,
  canViewArn,
}) => {
  const [data, fields] = useMemo(() => {
    let fields: ReadonlyField[] = [
      { label: 'Type', prop: 'type' },
      { label: 'Status', prop: 'status' },
      { label: 'Status Message', prop: 'statusMessage' },
      { label: 'Supported Features', prop: 'supportedFeatures' },
      { label: 'Configuration', prop: 'config-section', propType: 'section' },
    ];

    const typedConnector = getTypedConnector(connector);
    const { type, typeLabel, status, error, supportedFeatures, config, deprecated } =
      typedConnector;

    const activeFeatures = Object.keys(supportedFeatures).filter(
      (featureId) => supportedFeatures[featureId as keyof typeof supportedFeatures]
    );

    let data: JsonObject = {
      type: (
        <>
          {typeLabel} {deprecated && <StatusChip value="deprecated" size="x-small" />}
        </>
      ),
      status: <StatusChip value={status} size="small" />,
      statusMessage: error ? <InlineToast hideIcon message={error} level="error" /> : <NoneChip />,
      supportedFeatures: (
        <Stack direction="row" gap={1}>
          {activeFeatures.map((featureId) => {
            const label = ConnectorFeatureLabels[featureId as ConnectorFeature] || featureId;
            return <Chip key={featureId} label={label} />;
          })}
        </Stack>
      ),
      privateModels: <PrivateModelsCard models={privateModels} />,
      contextProviders: <ContextProvidersCard contexts={contextProviders} />,
    };

    const proxyConfig = config as ProxyConfig;
    const ConnectorUserEl = proxyConfig.user ? (
      <UserLink user={new UserModel(proxyConfig.user)} variant="outlined" />
    ) : (
      <NoneChip />
    );

    switch (type) {
      case 'aws':
        {
          const {
            authType,
            awsAccountId,
            crossAccountRoleARN,
            awsRegion,
            externalId,
            accessKeyIdLast4,
          } = config as AwsConfig;
          const isAccessKeys = authType === 'access-keys';

          fields.push({ label: 'Authentication Type', prop: 'authType' });
          data.authType = isAccessKeys ? 'Access Keys' : 'IAM Role Assumption';

          fields.push({ label: 'AWS Region', prop: 'awsRegion' });
          data.awsRegion = awsRegion || <NoneChip />;

          if (isAccessKeys) {
            const hasAccessKeyPreview = !!accessKeyIdLast4;

            fields.push({ label: 'Access Key ID', prop: 'accessKeyId' });
            data.accessKeyId = hasAccessKeyPreview ? (
              <NoneChip redact length={16} hint={accessKeyIdLast4} />
            ) : (
              <NoneChip notAvailable />
            );
          } else {
            const hasArn = !!crossAccountRoleARN;
            const hasExternalId = !!externalId;

            fields.push({ label: 'AWS Account ID', prop: 'awsAccountId' });
            data.awsAccountId = awsAccountId || <NoneChip />;

            if (canViewArn) {
              fields.push({ label: 'Cross Account Role ARN', prop: 'crossAccountRoleARN' });

              data.crossAccountRoleARN = hasArn ? (
                <TextInput
                  name="cross-account-role-arn"
                  disabled
                  copyClip
                  value={crossAccountRoleARN}
                  fullWidth
                  size="small"
                />
              ) : (
                <NoneChip />
              );

              fields.push({ label: 'External Id', prop: 'externalId' });

              data.externalId = hasExternalId ? (
                <TextInput
                  name="external-id"
                  disabled
                  copyClip
                  value={externalId}
                  fullWidth
                  size="small"
                />
              ) : (
                <NoneChip />
              );
            }
          }
        }
        break;
      case 'sharepoint-online':
        {
          const { tenantId, clientId, sharepointSite } = config as SharepointConfig;

          fields = [
            ...fields,
            { label: 'Tenant Id', prop: 'tenantId' },
            { label: 'Client Id', prop: 'clientId' },
            { label: 'SharePoint Base URL', prop: 'sharepointSite' },
          ];

          data = {
            ...data,
            tenantId,
            clientId,
            sharepointSite,
          };
        }
        break;
      case 'webcrawler':
        {
          const { url } = config as WebcrawlerConfig;

          fields.push({ label: 'URL', prop: 'url' });
          data.url = url;
        }
        break;
      default:
        fields.push({ label: '', prop: 'noConfig' });
        data.noConfig = <NoneChip />;

        break;
      case 'zscaler':
        {
          const { xAuthenticatedUserEnabled } = config as ZscalerConfig;

          fields.push({
            label: 'X-Authenticated-User Header',
            prop: 'xAuthenticatedUserEnabled',
          });
          data.xAuthenticatedUserEnabled = (
            <StatusChip value={xAuthenticatedUserEnabled ? 'enabled' : 'disabled'} />
          );
        }
        break;
      case 'netskope':
        {
          const { xAuthenticatedUserEnabled, tenantInfo } = config as NetskopeConfig;

          fields = [
            ...fields,
            {
              label: 'X-Authenticated-User Header',
              prop: 'xAuthenticatedUserEnabled',
            },
            {
              label: 'Tenant Info',
              prop: 'tenantInfo',
            },
          ];

          data.tenantInfo = tenantInfo || <NoneChip />;

          data.xAuthenticatedUserEnabled = (
            <StatusChip value={xAuthenticatedUserEnabled ? 'enabled' : 'disabled'} />
          );
        }
        break;

      case 'proxy':
        {
          const { hasConnectorAuthentication } = typedConnector as ProxyConnectorModel;
          const { authenticationType, emailProxyIdEnabled, sourceCIDRs } = config as ProxyConfig;

          fields = [
            ...fields,
            {
              label: 'Authentication Type',
              prop: 'authenticationType',
            },
            ...(hasConnectorAuthentication
              ? [
                  {
                    label: 'Connector User',
                    prop: 'user',
                  },
                  {
                    label: 'Allow Email Identification in PAC File URL',
                    prop: 'emailProxyIdEnabled',
                  },
                ]
              : []),
            {
              label: 'Source IP Ranges',
              prop: 'sourceCIDRs',
            },
          ];

          const authTypeOpt = ProxyConnectorAuthTypeOptions.find(
            ({ value }) => value === authenticationType
          );

          data.user = ConnectorUserEl;
          data.authenticationType = authTypeOpt?.label || authenticationType;
          data.emailProxyIdEnabled = <StatusChip value={!!emailProxyIdEnabled} />;
          data.sourceCIDRs = sourceCIDRs?.length ? (
            <Stack gap={1}>
              {sourceCIDRs.map(({ cidr, status }, index) => (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-start"
                  key={cidr}
                  gap={1}
                >
                  <Box width={200}>
                    <TextInput
                      copyClip
                      value={cidr}
                      name="cidr"
                      size="x-small"
                      readonly
                      fullWidth
                    />
                  </Box>
                  <StatusChip value={status} key={index} size="small" />
                </Stack>
              ))}
            </Stack>
          ) : (
            <NoneChip />
          );
        }
        break;

      case 'gemini':
      case 'openai':
      case 'brave-search':
      case 'perplexity':
      case 'nvidia-nim':
      case 'anthropic':
        {
          const { apiKeyLast4 } = config as ConnectorApiKeyConfig;
          const hasApiKeyPreview = !!apiKeyLast4;

          fields = [
            ...fields,
            {
              label: 'API Key',
              prop: 'apiKey',
            },
          ];

          data.apiKey = <NoneChip redact length={32} />;

          data.apiKey = hasApiKeyPreview ? (
            <NoneChip redact length={32} hint={apiKeyLast4} />
          ) : (
            <NoneChip notAvailable />
          );
        }
        break;

      case 'azure-openai':
        {
          fields = [
            ...fields,
            {
              label: 'API Key',
              prop: 'apiKey',
            },
            {
              label: 'Account name',
              prop: 'accountName',
            },
          ];

          data.apiKey = <NoneChip redact length={32} />;
          data.accountName = (typedConnector as AzureOpenAIConnectorModel).accountName;
        }
        break;
      case 'azure-foundry':
        {
          const { apiKeyLast4, resourceName, projectName } = config as AzureFoundryConfig;
          const hasApiKeyPreview = !!apiKeyLast4;

          fields = [
            ...fields,
            {
              label: 'API Key',
              prop: 'apiKey',
            },
            {
              label: 'Resource Name',
              prop: 'resourceName',
            },
            {
              label: 'Project Name',
              prop: 'projectName',
            },
          ];

          data.apiKey = hasApiKeyPreview ? (
            <NoneChip redact length={32} hint={apiKeyLast4} />
          ) : (
            <NoneChip notAvailable />
          );
          data.resourceName = resourceName || <NoneChip />;
          data.projectName = projectName || <NoneChip />;
        }
        break;
      case 'vertex-ai':
        {
          fields = [
            ...fields,
            {
              label: 'Service Account JSON',
              prop: 'serviceAccountJson',
            },
          ];

          data.serviceAccountJson = <NoneChip redact length={32} />;
        }
        break;
      case 'cloudflare':
        {
          const { hasConnectorAuthentication } = typedConnector as CloudflareConnectorModel;
          const { authenticationType, xAuthenticatedUserEnabled } = config as CloudflareConfig;

          fields = [
            ...fields,
            {
              label: 'Authentication Type',
              prop: 'authenticationType',
            },
            {
              label: 'X-Authenticated-User Header',
              prop: 'xAuthenticatedUserEnabled',
            },
            ...(hasConnectorAuthentication
              ? [
                  {
                    label: 'Connector User',
                    prop: 'user',
                  },
                ]
              : []),
          ];

          data.xAuthenticatedUserEnabled = (
            <StatusChip value={xAuthenticatedUserEnabled ? 'enabled' : 'disabled'} />
          );

          data.user = ConnectorUserEl;

          const authTypeOpt = ProxyConnectorAuthTypeOptions.find(
            ({ value }) => value === authenticationType
          );
          data.authenticationType = authTypeOpt?.label || authenticationType;
        }
        break;

      case 'cloudflare-ai':
        {
          const { apiKeyLast4, accountId } = config as CloudflareAIConfig;
          const hasApiKeyPreview = !!apiKeyLast4;

          fields = [
            ...fields,
            {
              label: 'Account ID',
              prop: 'accountId',
            },
            {
              label: 'API Key',
              prop: 'apiKey',
            },
          ];

          data.accountId = accountId || <NoneChip />;

          data.apiKey = hasApiKeyPreview ? (
            <NoneChip redact length={32} hint={apiKeyLast4} />
          ) : (
            <NoneChip notAvailable />
          );
        }
        break;

      case 'tailscale':
        {
          const { tailnetId, xAuthenticatedUserEnabled, user } = config as TailscaleConfig;

          fields = [
            ...fields,
            { label: 'Tailnet ID', prop: 'tailnetId' },
            { label: 'Enable Tailnet Identity', prop: 'xAuthenticatedUserEnabled' },
            ...(user
              ? [
                  {
                    label: 'Connector User',
                    prop: 'user',
                  },
                ]
              : []),
          ];
          data.tailnetId = tailnetId;
          data.xAuthenticatedUserEnabled = (
            <StatusChip value={xAuthenticatedUserEnabled ? 'enabled' : 'disabled'} />
          );
          data.user = ConnectorUserEl;
        }
        break;
      case 'app':
        {
          const { connectorId, user } = config as AppConfig;

          fields = [
            ...fields,
            ...(user
              ? [
                  {
                    label: 'Connector User',
                    prop: 'user',
                  },
                ]
              : []),
            { label: 'API Key', prop: 'connectorId' },
          ];

          data.connectorId = (
            <TextInput
              name="connectorId"
              readonly
              copyClip
              value={connectorId}
              fullWidth
              size="x-small"
            />
          );

          data.user = ConnectorUserEl;
        }
        break;

      case 'doh':
        // DoH has no configurable settings, only deployment endpoints
        fields = fields.filter(({ prop }) => prop !== 'config-section');
        break;
    }
    // add usage fields
    const { privateModels: pmFeature, contextData: cdFeature } = supportedFeatures;
    if (pmFeature || cdFeature) {
      fields.push({ label: 'Usage', prop: 'usage-section', propType: 'section' });
      if (pmFeature) {
        fields.push({ label: '', prop: 'privateModels' });
      }

      if (cdFeature) {
        fields.push({ label: '', prop: 'contextProviders' });
      }
    }

    return [data, fields];
  }, [connector, privateModels, contextProviders, canViewArn]);

  return <ReadonlyView fields={fields} data={data} />;
};

export default ConnectorReadonlyView;
